import type { AcademyAnnouncementBannerThemeSettings } from '../../types';
import type { IconNames } from '@workramp/ui';
import { Badge, Checkbox, Col, Option, Row, Select, TextField } from '@workramp/ui';
import { debounce } from 'underscore';
import { useCallback, useMemo, useState } from 'react';
import {
  ColorPickerSetting,
  RichTextSetting,
  SettingsSidebarSubSection,
  SettingsSidebarSubSectionCard,
} from '~/clc/features/theming/admin/components';
import { isValidUrl } from '~/common';
import { useIsWithinTimeRange } from '../../utils';
import { AnnouncementBannerVisibilitySettings } from './AnnouncementBannerVisibilitySettings';

const AnnouncementBannerSettings = ({
  settings,
  onChange,
}: {
  settings: {
    startTime: number;
    endTime: number;
    themeSettings: AcademyAnnouncementBannerThemeSettings;
  };
  onChange: (val: {
    startTime?: number;
    endTime?: number;
    themeSettings?: AcademyAnnouncementBannerThemeSettings;
  }) => void;
}) => {
  const [linkUrlFieldErrorMessage, setLinkUrlFieldErrorMessage] = useState('');
  const handleThemeChange = useCallback(
    (key, val) => {
      onChange({
        themeSettings: {
          ...settings.themeSettings,
          [key]: val,
        },
      });
    },
    [onChange, settings.themeSettings],
  );

  const handleDebouncedThemeChange = useMemo(
    () =>
      debounce((key, val) => {
        if (!validateUpdateData(key, val)) return;

        handleThemeChange(key, val);
      }, 1000),
    [handleThemeChange],
  );

  const validateUpdateData = (key, value) => {
    let isValid = true;

    if (key === 'linkUrl') {
      if (!value) {
        setLinkUrlFieldErrorMessage('');
        return true;
      }

      if (!isValidUrl(value)) {
        setLinkUrlFieldErrorMessage(`Link URL must be a valid URL.`);
        isValid = false;
      } else {
        setLinkUrlFieldErrorMessage('');
      }
    }

    return isValid;
  };

  const isPublished = useIsWithinTimeRange({ startTime: settings.startTime, endTime: settings.endTime });

  const getBadge = () => {
    // Published
    if (isPublished) return <Badge variant="success">• Live</Badge>;

    // Scheduled
    if (settings.startTime && Date.now() < settings.startTime) return <Badge variant="info">Scheduled</Badge>;

    return <Badge variant="secondary">Disabled</Badge>;
  };
  return (
    <div style={{ paddingTop: '20px', paddingBottom: '20px' }}>
      <SettingsSidebarSubSection>
        <SettingsSidebarSubSectionCard title="Visibility" description={getBadge()}>
          <AnnouncementBannerVisibilitySettings
            isPublished={isPublished}
            startTime={settings.startTime ?? undefined}
            endTime={settings.endTime ?? undefined}
            onChange={onChange}
          />
        </SettingsSidebarSubSectionCard>
        <SettingsSidebarSubSectionCard title="Message">
          <Row gapX={3}>
            <Col>
              <ColorPickerSetting
                label="Background Color"
                allowAlpha={false}
                value={settings.themeSettings.backgroundColor}
                onChange={(val) => handleThemeChange('backgroundColor', val)}
              />
            </Col>
            <Col>
              <ColorPickerSetting
                label="Text Color"
                allowAlpha={false}
                value={settings.themeSettings.textColor}
                onChange={(val) => handleThemeChange('textColor', val)}
              />
            </Col>
          </Row>
          <RichTextSetting
            label="Message"
            defaultValue={settings.themeSettings.message}
            onChange={(val) => handleDebouncedThemeChange('message', val)}
          />
        </SettingsSidebarSubSectionCard>
        <SettingsSidebarSubSectionCard title="Link">
          <Select
            title="Link Type"
            label="Link Type"
            defaultSelectedKey={settings?.themeSettings.linkType}
            onSelectionChange={(val) => handleThemeChange('linkType', val)}
          >
            {[
              { label: 'None', val: 'none' },
              { label: 'Button', val: 'button' },
              { label: 'Link', val: 'link' },
            ].map(({ label, val }) => (
              <Option key={val}>{label}</Option>
            ))}
          </Select>
          {settings?.themeSettings.linkType !== 'none' && (
            <>
              <ColorPickerSetting
                label="Text Color"
                allowAlpha={false}
                value={settings.themeSettings.linkTextColor}
                onChange={(val) => handleThemeChange('linkTextColor', val)}
              />
              {settings?.themeSettings.linkType === 'button' && (
                <Row gapX={3}>
                  <Col>
                    <ColorPickerSetting
                      label="Background Color"
                      allowAlpha={false}
                      value={settings.themeSettings.linkBackgroundColor}
                      onChange={(val) => handleThemeChange('linkBackgroundColor', val)}
                    />
                  </Col>
                  <Col>
                    <ColorPickerSetting
                      label="Border Color"
                      allowAlpha={false}
                      value={settings.themeSettings.linkBorderColor}
                      onChange={(val) => handleThemeChange('linkBorderColor', val)}
                    />
                  </Col>
                </Row>
              )}
              <TextField
                label="Text"
                defaultValue={settings.themeSettings.linkText}
                onChange={(val) => handleDebouncedThemeChange('linkText', val)}
                placeholder="Click here"
              />
              <TextField
                label="URL"
                defaultValue={settings.themeSettings.linkUrl}
                onChange={(val) => handleDebouncedThemeChange('linkUrl', val)}
                placeholder="http://"
                errorMessage={linkUrlFieldErrorMessage}
                hasError={!!linkUrlFieldErrorMessage}
              />
              <Checkbox
                onChange={(val) => handleThemeChange('linkOpenInNewTab', val)}
                isSelected={settings.themeSettings.linkOpenInNewTab}
              >
                Open in new tab
              </Checkbox>
            </>
          )}
        </SettingsSidebarSubSectionCard>
      </SettingsSidebarSubSection>
    </div>
  );
};

export const AnnouncementBannerSectionEditorSettings: {
  name: string;
  key: string;
  iconName: IconNames;
  settings: any;
} = {
  name: 'Banner',
  key: 'announcement-bar',
  iconName: 'announcement:regular',
  settings: AnnouncementBannerSettings,
};
